import { useQuery } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import useDialog from 'hooks/Shared/useDialog'
import { selectErrorDialog } from 'lib/helpers/errors.helpers'
import { setSelectedInvoice } from 'actions/invoices'
import {
  getCfdiById,
  getCfdiAmountPaid,
  getCfdiListFilesInZip,
} from 'services/Invoices/invoices.services'
import { downloadFileFromData } from 'lib/helpers/utilities'
import ErrorI from '@material-ui/icons/ErrorOutline'
import { IInvoice } from 'models/invoices.models'
import { QueryOptions } from 'models/general'
import { Money } from 'ts-money'
import { TModalIcon } from 'components/Shared/SydDialog/SydDialog'

export const useCfdi = (
  workspaceId: number,
  id: number,
  options: QueryOptions<IInvoice> = {}
) => {
  const dispatch = useDispatch()

  const { noSave = false, ...rest } = options

  const query = useQuery<IInvoice, any>(
    ['get-cfdi', workspaceId, id],
    () => {
      return getCfdiById(workspaceId, id)
    },
    {
      ...rest,
      onSuccess: data => {
        !noSave && dispatch(setSelectedInvoice(data))
      },
    }
  )

  return query
}

export const useCfdiPaidAmount = (
  workspaceId: number,
  invoice: IInvoice | null,
  options: QueryOptions<Money> = {}
) => {
  const { onSuccess, ...rest } = options

  const query = useQuery<Money, any>(
    ['get-cfdi-paid-amount', workspaceId, invoice],
    () => {
      if (!invoice) throw new Error('CFDI cargado incorrectamente')
      return getCfdiAmountPaid(workspaceId, invoice)
    },
    {
      ...rest,
      onSuccess: data => {
        onSuccess && onSuccess(data)
      },
    }
  )

  return query
}

export const useDownloadInvoicesFilesInZip = (
  workspaceId: number,
  page: number,
  size: number,
  rfc?: string,
  search?: string,
  advance?: boolean,
  type?: string,
  status?: number,
  startDate?: string | null,
  endDate?: string | null,
  receptor?: string,
  options: QueryOptions<string> = {}
) => {
  const dialog = useDialog()

  const {
    onBefore,
    onSuccess,
    onError,
    noDialog = false,
    refreshOn = [],
    ...rest
  } = options

  const query = useQuery<string, any>(
    ['get-cfdi-list', workspaceId, ...refreshOn],
    () => {
      onBefore && onBefore()
      return getCfdiListFilesInZip(
        workspaceId,
        page,
        size,
        rfc,
        search,
        advance,
        type,
        status,
        startDate || undefined,
        endDate || undefined,
        receptor
      )
    },
    {
      ...rest,
      onSuccess: file => {
        onSuccess && onSuccess(file)
        downloadFileFromData(
          'Facturas.zip',
          `data:application/zip;base64,${file}`
        )
      },
      onError: async error => {
        const op = selectErrorDialog(error)
        if (op?.variant) {
          await dialog[op.variant](op)
          return
        }

        if (!noDialog) {
          const rp = await dialog.dangerAsync({
            title: 'generic.error.title',
            subtitle: 'No se pudieron descargar los CFDIs seleccionados.',
            icon: ErrorI as TModalIcon,
            primaryButtonText: 'Reintentar',
            secondaryButtonText: 'Cancelar',
          })

          if (rp) {
            query.refetch()
            return
          }
        }

        onError && onError(error)
      },
    }
  )

  return query
}
